/**
 * Deeper markets (roadmap P3, docs/MARKETS.md): the catalogue and pure maths of the big contested buyers,
 * the temporary high-value buyers and a seller's standing with a district's buyers. Shared by the server
 * (the only thing that applies these numbers to money or goods) and the client UI (panels, the black book).
 *
 * The quality-tier part of demand (premiums per buyer kind, smaller markets for better grades) lives with
 * the rest of the sale maths in shared/economy.ts (`QUALITY_PREMIUM`, `QUALITY_SATURATION`, `QUALITY_SPILL`).
 */
import { DISTRICT_IDS, DISTRICT_PREFERENCE, policeAttention, policeFactor, QUALITY_PREMIUM, qualityTier, type EconDistrictId, type Place, type QualityTier } from './economy';
import { getItemDef } from './items';

const HOUR = 3_600_000;
const MIN = 60_000;

// ----------------------------------------------------------------------------- contract weeks

export const WEEK_MS = 7 * 24 * HOUR;
/** Contract weeks start on Monday 00:00 UTC (1970-01-05 was a Monday). */
const WEEK_EPOCH = 4 * 24 * HOUR;
export const weekIndex = (now: number): number => Math.floor((now - WEEK_EPOCH) / WEEK_MS);
export const weekStart = (week: number): number => WEEK_EPOCH + week * WEEK_MS;
export const weekEndsAt = (now: number): number => weekStart(weekIndex(now) + 1);

export interface MarketQuote {
  /** Whole pounds for the lot. */
  total: number;
  /** What the first unit fetches. */
  unitFirst: number;
  /** Heat points the sale raises (before police witnesses / crew sharing). */
  heat: number;
}

/** Goods made by production properties (shared/properties.ts) plus the odd hot item: what the new demand is FOR. */
export const MARKET_GOODS = ['tape-decks', 'tax-discs', 'cut-spirits', 'hooky-cigs', 'hot-goods'] as const;

// ----------------------------------------------------------------------------- big (contested) buyers

/**
 * One big buyer per district: takes produced goods by the lorry-load at a good price, but only so many a
 * week, and every crew and gang in the city is after the same capacity. First come; as the week fills he
 * gets choosy (only Fine, then only Prime), so the best goods win the end of the week.
 */
export interface BigBuyerDef extends Place {
  kind: 'buyer';
  district: EconDistrictId;
  /** Def ids he takes (produced goods only: no supplier good, so no buy-low/sell-high loop). */
  wants: readonly string[];
  /** Units he takes per contract week, all goods together. */
  weeklyUnits: number;
}

export const BIG_TUNING = {
  /** On top of district preference and the grade premium (`QUALITY_PREMIUM.big`). */
  priceMul: 1.1,
  /** The price slides by up to this share as the week's capacity fills (a full week = −25 %). */
  fillSlide: 0.25,
  /** Once this share of the week is taken he only wants Fine or better; at the second, Prime only. */
  gradeSteps: [0.5, 0.8] as const,
  /** Most of a week one seller (a gang, or an independent) may take: base, plus up to `shareBonus` × standing/cap. */
  shareBase: 0.35,
  shareBonus: 0.15,
  /** His go-between's fee for the introduction: once per seller per buyer per week (a money SINK, `market-intro`). */
  introFee: 300,
  maxPerSale: 40,
  heatFlat: 4,
  heatPerSuspicion: 0.8,
} as const;

export const BIG_BUYERS: readonly BigBuyerDef[] = [
  { id: 'big-anchor', kind: 'buyer', poi: 'pub-crooked-anchor', name: "Mags Delaney's cellarman", reach: 6, district: 'the-parade', wants: ['tape-decks', 'cut-spirits', 'hooky-cigs'], weeklyUnits: 160 },
  { id: 'big-halloran', kind: 'buyer', poi: 'garage-halloran', name: "Halloran's export man", reach: 10, district: 'cinder-yards', wants: ['tax-discs', 'tape-decks', 'hot-goods'], weeklyUnits: 160 },
  { id: 'big-pilcher', kind: 'buyer', poi: 'lockup-pilcher-7', name: 'The tallyman of Pilcher Ginnel', reach: 6, district: 'tanners-row', wants: ['hooky-cigs', 'tape-decks', 'hot-goods'], weeklyUnits: 160 },
  { id: 'big-mercy', kind: 'buyer', poi: 'mercy-square', name: 'The man in the black Regent, Mercy Square', reach: 6, district: 'mercy-square', wants: ['tape-decks', 'cut-spirits', 'tax-discs'], weeklyUnits: 160 },
  { id: 'big-pike', kind: 'buyer', poi: 'respray-pike', name: "Pike's brother-in-law (lorry-loads)", reach: 8, district: 'gullet-basin', wants: ['cut-spirits', 'tax-discs', 'hooky-cigs'], weeklyUnits: 160 },
];

export const bigBuyer = (id: string): BigBuyerDef | undefined => BIG_BUYERS.find((b) => b.id === id);

/** The worst grade he still takes with `used` of `capacity` gone this week (0 Standard, 1 Fine, 2 Prime). */
export function bigMinGrade(used: number, capacity: number): QualityTier {
  const fill = capacity > 0 ? used / capacity : 1;
  return fill >= BIG_TUNING.gradeSteps[1] ? 2 : fill >= BIG_TUNING.gradeSteps[0] ? 1 : 0;
}

/** Units of one week one seller may take, by their standing in his district (0..STANDING.cap). */
export function bigShareCap(capacity: number, standing: number): number {
  const s = Math.max(0, Math.min(STANDING.cap, standing)) / STANDING.cap;
  return Math.floor(capacity * (BIG_TUNING.shareBase + BIG_TUNING.shareBonus * s));
}

/** `leadBonus`: the territory edge of the firm leading his district (docs/TERRITORY.md), on top of standing (at most 10 %). */
/** `priceMul` / `attentionAdd`: the Kessmouth week's modifier (shared/week.ts), bounded there. */
export function quoteBig(args: { buyer: BigBuyerDef; defId: string; quality: number; qty: number; used: number; districtHeat: number; standingBonus?: number; leadBonus?: number; priceMul?: number; attentionAdd?: number }): MarketQuote {
  const def = getItemDef(args.defId);
  if (!def || !args.buyer.wants.includes(args.defId)) return { total: 0, unitFirst: 0, heat: 0 };
  const tier = qualityTier(args.quality);
  const pref = DISTRICT_PREFERENCE[args.buyer.district][args.defId] ?? 1;
  const attention = Math.min(1, policeAttention(args.buyer.district, args.districtHeat) + Math.max(0, args.attentionAdd ?? 0));
  const base = def.baseValue * QUALITY_PREMIUM.big[tier] * pref * BIG_TUNING.priceMul * policeFactor(attention) * (1 + Math.max(0, Math.min(STANDING.maxBonus, args.standingBonus ?? 0)) + Math.max(0, Math.min(0.1, args.leadBonus ?? 0))) * Math.max(0, args.priceMul ?? 1);
  let total = 0;
  let first = 0;
  for (let i = 0; i < args.qty; i++) {
    const fill = Math.min(1, (args.used + i) / Math.max(1, args.buyer.weeklyUnits));
    const unit = base * (1 - BIG_TUNING.fillSlide * fill);
    if (i === 0) first = unit;
    total += unit;
  }
  const heat = (BIG_TUNING.heatFlat + BIG_TUNING.heatPerSuspicion * def.suspicion * args.qty) * (0.5 + attention);
  return { total: Math.floor(total), unitFirst: Math.floor(first), heat };
}

// ----------------------------------------------------------------------------- temporary high-value buyers

/**
 * Where a buyer from out of town turns up: somewhere risky (under the law's nose, out on the quay), and
 * never within `TEMP_TUNING.minSupplierDistance` of a supplier (no buy-and-sell-on-the-spot loop).
 */
export interface TempSpotDef extends Place {
  kind: 'buyer';
  /** How the word on the street describes the place. */
  where: string;
}

export const TEMP_SPOTS: readonly TempSpotDef[] = [
  { id: 'spot-nick', kind: 'buyer', poi: 'police-station', name: 'Round the back of the nick', where: 'round the back of the police station, bold as brass', reach: 8 },
  { id: 'spot-bank', kind: 'buyer', poi: 'bank-savings', name: 'Outside the Savings Bank', where: 'in a parked Grandee outside the Savings Bank', reach: 8 },
  { id: 'spot-quay', kind: 'buyer', poi: 'docks-crane-quay', name: 'Out on Crane Quay', where: 'on Crane Quay before the tide turns', reach: 8 },
  { id: 'spot-brannoc', kind: 'buyer', poi: 'st-brannocs', name: "Behind St. Brannoc's", where: "in the churchyard behind St. Brannoc's", reach: 8 },
  { id: 'spot-coalgate', kind: 'buyer', poi: 'phone-coalgate', name: 'By the Coalgate phone box', where: 'by the phone box on Coalgate', reach: 8 },
];

export const tempSpot = (id: string): TempSpotDef | undefined => TEMP_SPOTS.find((s) => s.id === id);

export const TEMP_NAMES = ['A flash buyer from up West', 'A Brummie with an empty lorry', "A ship's mate with cash to burn", 'A Soho man in a camel coat'] as const;

export const TEMP_TUNING = {
  /** Gap between one buyer leaving and the next being heard of. */
  gapMinMs: 45 * MIN,
  gapMaxMs: 75 * MIN,
  /** Word goes round this long before he is there (telegraphed: time to fetch the goods — and for rivals to hear it too). */
  leadMs: 5 * MIN,
  /** He stays this long. */
  openMs: 20 * MIN,
  /** Units he takes before he is gone. */
  capacity: 30,
  /** Most of one buyer one seller (gang / independent) may take. */
  shareMax: 0.6,
  maxPerSale: 15,
  /** On top of district preference and the grade premium (`QUALITY_PREMIUM.temp`). */
  priceMul: 1.35,
  fillSlide: 0.2,
  heatFlat: 8,
  heatPerSuspicion: 1.3,
  /** Selling in the open somewhere watched: everything about the sale is this much hotter. */
  heatMul: 1.5,
  /** Spots are at least this far from any supplier (metres). */
  minSupplierDistance: 100,
} as const;

export interface TempPlan {
  spot: string;
  name: string;
  wants: string[];
  capacity: number;
  announceAt: number;
  startsAt: number;
  endsAt: number;
}

/** The next temporary buyer: where, who, what (two goods), when. Deterministic for a given `rng`. */
export function planTemp(rng: () => number, now: number): TempPlan {
  const pick = <T>(xs: readonly T[]): T => xs[Math.min(xs.length - 1, Math.floor(rng() * xs.length))]!;
  const spot = pick(TEMP_SPOTS);
  const first = pick(MARKET_GOODS.slice(0, 4)); // always one produced good
  const rest = MARKET_GOODS.filter((g) => g !== first);
  const second = pick(rest);
  const startsAt = now + TEMP_TUNING.leadMs;
  return { spot: spot.id, name: pick(TEMP_NAMES), wants: [first, second], capacity: TEMP_TUNING.capacity, announceAt: now, startsAt, endsAt: startsAt + TEMP_TUNING.openMs };
}

/** When the next buyer is heard of after one left at `endedAt`. */
export function nextTempAt(rng: () => number, endedAt: number): number {
  return endedAt + TEMP_TUNING.gapMinMs + Math.floor(rng() * (TEMP_TUNING.gapMaxMs - TEMP_TUNING.gapMinMs));
}

export function quoteTemp(args: { defId: string; wants: readonly string[]; quality: number; qty: number; sold: number; capacity: number; district: EconDistrictId; districtHeat: number; priceMul?: number; attentionAdd?: number }): MarketQuote {
  const def = getItemDef(args.defId);
  if (!def || !args.wants.includes(args.defId)) return { total: 0, unitFirst: 0, heat: 0 };
  const tier = qualityTier(args.quality);
  const pref = DISTRICT_PREFERENCE[args.district][args.defId] ?? 1;
  const attention = Math.min(1, policeAttention(args.district, args.districtHeat) + Math.max(0, args.attentionAdd ?? 0));
  const base = def.baseValue * QUALITY_PREMIUM.temp[tier] * pref * TEMP_TUNING.priceMul * policeFactor(attention) * Math.max(0, args.priceMul ?? 1);
  let total = 0;
  let first = 0;
  for (let i = 0; i < args.qty; i++) {
    const unit = base * (1 - TEMP_TUNING.fillSlide * Math.min(1, (args.sold + i) / Math.max(1, args.capacity)));
    if (i === 0) first = unit;
    total += unit;
  }
  const heat = (TEMP_TUNING.heatFlat + TEMP_TUNING.heatPerSuspicion * def.suspicion * args.qty) * (0.5 + attention) * TEMP_TUNING.heatMul;
  return { total: Math.floor(total), unitFirst: Math.floor(first), heat };
}

/** Units of one temporary buyer one seller may take. */
export const tempShareCap = (capacity: number): number => Math.max(1, Math.floor(capacity * TEMP_TUNING.shareMax));

// ----------------------------------------------------------------------------- standing (gang influence, preparing P4)

/**
 * A seller's standing with a district's buyers: earned by selling there, decaying (half-life a day), capped,
 * with diminishing returns and a challenger bonus. A seller is a gang (all its members' sales count) or an
 * independent character — independents earn it too and are never locked out of any buyer.
 */
export const STANDING = {
  cap: 100,
  halfLifeMs: 24 * HOUR,
  /** Points per pound of sale value (before diminishing returns): £15 000 of sales from zero ≈ 63 points. */
  perPound: 1 / 150,
  /** A seller who is NOT the district's leader earns this much faster while someone else leads. */
  challengerMul: 1.5,
  /** Standing needed to count as the district's leader. */
  leaderMin: 10,
  /** Price bonus at full standing with district buyers (street, fence, big). */
  maxBonus: 0.06,
  /** Only a seller's best this-many districts give a price bonus (no one firm sets the price everywhere). */
  bonusDistricts: 2,
} as const;

/** Exponential time constant of the standing decay. */
export const STANDING_TAU_MS = STANDING.halfLifeMs / Math.LN2;

/** Seller identity: a gang (`g<id>`) or an independent character (`c<id>`). */
export const sellerKey = (gangId: number | null, cid: number): string => (gangId !== null ? `g${gangId}` : `c${cid}`);

/** Standing earned by a sale worth `value` with `current` standing (diminishing: each point costs more). */
export function standingGain(value: number, current: number, challenger: boolean): number {
  const room = Math.max(0, STANDING.cap - current);
  const raw = Math.max(0, value) * STANDING.perPound * (room / STANDING.cap) * (challenger ? STANDING.challengerMul : 1);
  return Math.min(room, raw);
}

/**
 * Price bonus in `district` from a seller's standing everywhere: only their best `bonusDistricts`
 * districts count (ties broken by district order), scaled linearly to `maxBonus` at the cap.
 */
export function standingBonus(standings: Partial<Record<EconDistrictId, number>>, district: EconDistrictId): number {
  const mine = standings[district] ?? 0;
  if (mine <= 0) return 0;
  const at = DISTRICT_IDS.indexOf(district);
  const better = DISTRICT_IDS.filter((d, i) => d !== district && ((standings[d] ?? 0) > mine || ((standings[d] ?? 0) === mine && i < at))).length;
  if (better >= STANDING.bonusDistricts) return 0;
  return STANDING.maxBonus * Math.min(1, mine / STANDING.cap);
}

/** Plain words for a grade (UI and news). */
export const GRADE_NAMES = ['Standard', 'Fine', 'Prime'] as const;
